import {
    ComponentPublicInstance,
    PropType,
    Raw,
    defineComponent,
    getCurrentInstance,
    markRaw,
    onMounted,
    onUnmounted,
    ref,
    watchEffect,
} from 'vue'

import {
    CsCopyTextLink,
    CsDataSheet,
    CsTabs,
    CsTag,
    E_Mobile_Ad,
    E_Resultlist_Disturber_Ad_1,
    MsCroppedCopy,
    MsHeadline,
    MsTextLink,
} from '@sunrise/components'
import { MsHydration } from '@sunrise/hydration'
import {
    MsCheckCircle,
    MsDoor,
    MsEuronote,
    MsFilePdf,
    MsFloorPlan,
    MsImmo,
    MsLocator,
    MsPriceTag,
    MsRent,
    MsRentCold,
    MsRentSqm,
    MsShop,
    MsSurface,
    MsSurfaceArrows,
} from '@sunrise/icons'
import { getComponentByName, screenSizeDetection, useGlobalStore } from '@sunrise/shared'
import { Cms, Components } from '@sunrise/types'
import { GalleryAd } from '@sunrise/types/dist/components/CsGallery'

import { List_Wrap } from '../../../../components/MsCheckmarkList/MsCheckmarkList.enum'
import { ContactBtnPosition } from '../../../../components/MsDetailContactBtnGroup/MsDetailContactBtnGroup.enum'
import MsDetailContactInfo from '../../../../components/MsDetailContactInfo/MsDetailContactInfo.vue'
import MsDetailFloorPlan from '../../../../components/MsDetailFloorPlan/MsDetailFloorPlan.vue'
import MsDetailGallery from '../../../../components/MsDetailGallery/MsDetailGallery.vue'
import MsDetailStickyBar from '../../../../components/MsDetailStickyBar/MsDetailStickyBar.vue'
import MsDetailUnavailable from '../../../../components/MsDetailUnavailable/MsDetailUnavailable.vue'
import MsExpanderBox from '../../../../components/MsExpanderBox/MsExpanderBox.vue'
import MsMap from '../../../../components/MsMap/MsMap.vue'
import MsShareBar from '../../../../components/MsShareBar/MsShareBar.vue'
import { PositionType, StickyPosition } from '../../../../components/MsStickyContainer/MsStickyContainer.enum'
import MsStickyContainer from '../../../../components/MsStickyContainer/MsStickyContainer.vue'

import {
    CarouselLabel,
    EstateType,
    KeyInfoLabel,
    DetailPageType as PageType,
} from '../../../../ts/mapper/decoratedEstateDetailMapper.enum'
import { DecoratedEstateDetail, Overview } from '../../../../ts/mapper/decoratedEstateDetailMapper.interface'
import { DecoratedEstate } from '../../../../ts/mapper/decoratedItemEstate.interface'
import { PartnerLinks } from '../../../../ts/mapper/partnerLinksMapper.interface'
import { OfferType } from '../../../../ts/service/realEstateService.enum'
import { Tuple } from '../../../../ts/service/realEstateService.interface'
import { useDetailStore } from '../../../../ts/store/detailStoreModule/detailStoreModule'

import { expanderBox } from '../M_Immo_Detail.enum'
import { ShareBarProps } from '../M_Immo_Detail.interface'
import {
    buildExpanderBoxTracking,
    createMlmViewTracking,
    mlmTrackDirectEntryClick,
    trackFinancingLinkClick,
    trackMapAnchorClick,
} from '../M_Immo_DetailTracking'
import MsCircle from './real-estate-circle-brand-circle-2.svg?component'
import MsHouse from './real-estate-house-brand-house-3.svg?component'

export default defineComponent({
    name: 'HydratedDetailOnline',
    components: {
        MsExpanderBox,
        CsCopyTextLink,
        CsDataSheet,
        CsTabs,
        CsTag,
        MsCroppedCopy,
        MsCheckCircle,
        MsDetailContactInfo,
        MsDetailGallery,
        MsDetailUnavailable,
        MsDetailStickyBar,
        MsStickyContainer,
        MsDetailFloorPlan,
        MsFilePdf,
        MsHeadline,
        MsHydration,
        MsLocator,
        MsMap,
        MsShareBar,
        MsTextLink,
        E_Resultlist_Disturber_Ad_1,
        E_Mobile_Ad,
        MsCircle,
        MsHouse,
        MsEuronote,
    },
    props: {
        adSlot: {
            type: Object as PropType<Cms.Content>,
        },
        galleryAds: {
            type: Array as PropType<GalleryAd[]>,
            default: () => [],
        },
        googleApiKey: {
            type: String,
            required: true,
        },
        estateDetail: {
            type: Object as PropType<DecoratedEstateDetail>,
            required: true,
        },
        estate: {
            type: Object as PropType<DecoratedEstate>,
            required: true,
        },
        isRealEstatePreview: {
            type: Boolean,
            required: true,
        },
        partnerLinks: {
            type: Object as PropType<PartnerLinks>,
            required: true,
        },
        isExternalReferer: {
            type: Boolean,
            required: true,
        },
        shareBarProps: {
            type: Object as PropType<ShareBarProps>,
            required: true,
        },
    },
    setup(props) {
        const componentName = getCurrentInstance()?.type.name || ''
        const { isScreenM, isScreenL } = screenSizeDetection()
        const requestContext = useGlobalStore().requestContext
        const currentUrl = requestContext?.url.href || ''
        const shareBarTopConfig = {
            showReport: {
                mobile: false,
                desktop: false,
            },
            showShare: {
                mobile: true,
                desktop: true,
            },
            showPrint: {
                mobile: false,
                desktop: true,
            },
        }
        const shareBarBottomConfig = {
            showReport: {
                mobile: true,
                desktop: true,
            },
            showShare: {
                mobile: false,
                desktop: true,
            },
            showPrint: {
                mobile: false,
                desktop: true,
            },
        }
        /* c8 ignore next */
        const mlmViewTrackingId: Record<string, string> | undefined = createMlmViewTracking(
            props.isExternalReferer,
            componentName,
            currentUrl,
            props.estateDetail.id,
            true,
        )
        const eligibleTypesForFinancingLink: EstateType[] = [
            EstateType.HOUSE,
            EstateType.APARTMENT,
            EstateType.PROPERTY,
        ]
        const exposeeHasFinancingLink: boolean =
            !!props.partnerLinks.financingLink &&
            props.estateDetail.offerType === OfferType.SELL &&
            eligibleTypesForFinancingLink.includes(props.estateDetail.estateType)

        const stickyBarIsVisible = ref<boolean>(!isScreenL.value)
        const elContactTop = ref<ComponentPublicInstance | null>(null)
        const elContactBottom = ref<HTMLDivElement | null>(null)
        const showTable = ref<boolean>(false)

        const keyInfo: Components.CsDataSheet.DataSheetEntry[] = props.estateDetail.keyInfo.map((keyInfo: Tuple) => {
            return {
                ...keyInfo,
                markedRawIconComponent: getKeyInfoIconAsRaw(keyInfo.label),
            }
        })
        let elContactTopObserver: IntersectionObserver | null
        let elContactBottomObserver: IntersectionObserver | null

        const keyDataOptions = props.estateDetail.overview?.map((overview) => overview.label.toString()) || []
        const selectedKeyDataOption = ref(keyDataOptions[0])

        function showItem(item: Overview): boolean {
            return item.label === selectedKeyDataOption.value
        }

        function toggleTable(): void {
            showTable.value = !showTable.value
        }

        function observeContactBoxVisibility(): void {
            if (isScreenL.value) {
                if (!elContactTop.value) return
                elContactTopObserver = new IntersectionObserver(checkIntersectContactTop)
                elContactTopObserver.observe(elContactTop.value.$el)
            } else {
                if (!elContactBottom.value) return
                elContactBottomObserver = new IntersectionObserver(checkIntersectContactBottom)
                elContactBottomObserver.observe(elContactBottom.value)
            }
        }

        function checkIntersectContactBottom(entries: IntersectionObserverEntry[]): void {
            entries.forEach((entry) => {
                if (entry.isIntersecting || entry.boundingClientRect.bottom >= 0) {
                    showStickyBar()
                } else {
                    hideStickyBar()
                }
            })
        }

        function checkIntersectContactTop(entries: IntersectionObserverEntry[]): void {
            entries.forEach((entry) => {
                if (entry.isIntersecting || entry.boundingClientRect.bottom >= 0) {
                    hideStickyBar()
                } else {
                    showStickyBar()
                }
            })
        }

        function showStickyBar(): void {
            stickyBarIsVisible.value = true
        }

        function hideStickyBar(): void {
            stickyBarIsVisible.value = false
        }

        if (props.estateDetail.pageType === PageType.DETAIL_ONLINE) {
            useDetailStore().$patch({
                offerType: props.estateDetail.offerType,
                estateType: props.estateDetail.estateType,
                contactInfo: {
                    title: props.estateDetail.contact.title,
                    name: props.estateDetail.contact.name,
                },
                exposeeId: props.estateDetail.id,
            })

            if (props.estateDetail.offerType === OfferType.WAZ && props.estateDetail.periodRentInformation) {
                const detailStore = useDetailStore()
                detailStore.addPeriodRentTuple(props.estateDetail.periodRentInformation)
            }
        }

        function expanderBoxTrackingData(variant: string) {
            return buildExpanderBoxTracking(props.estateDetail.id, variant)
        }

        function getKeyInfoIconAsRaw(keyInfoLabel: string): Raw<object> {
            switch (keyInfoLabel) {
                case KeyInfoLabel.SELL:
                    return markRaw(MsPriceTag)
                case KeyInfoLabel.RENT_COLD:
                    return markRaw(MsRentCold)
                case KeyInfoLabel.ROOMS:
                    return markRaw(MsDoor)
                case KeyInfoLabel.AREA:
                    return markRaw(MsSurfaceArrows)
                case KeyInfoLabel.LIVING_AREA:
                    return markRaw(MsFloorPlan)
                case KeyInfoLabel.GROUND_AREA:
                case KeyInfoLabel.TOTAL_AREA:
                    return markRaw(MsSurface)
                case KeyInfoLabel.SALES_AREA:
                    return markRaw(MsShop)
                case KeyInfoLabel.PRICE_PER_SQM:
                    return markRaw(MsRentSqm)
                case KeyInfoLabel.FIXED_RENT:
                case KeyInfoLabel.GROUND_RENT_PER_YEAR:
                case KeyInfoLabel.LEASE_PER_MONTH:
                case KeyInfoLabel.RENT:
                    return markRaw(MsRent)
                default:
                    return markRaw(MsImmo)
            }
        }

        onMounted(() => {
            watchEffect(() => {
                observeContactBoxVisibility()
            })
            if (props.isExternalReferer) {
                mlmTrackDirectEntryClick(componentName, currentUrl, props.estateDetail.id, true)
            }
        })

        onUnmounted(() => {
            if (elContactTopObserver && elContactTop.value) {
                elContactTopObserver.unobserve(elContactTop.value.$el)
            }

            if (elContactBottomObserver && elContactBottom.value) {
                elContactBottomObserver.unobserve(elContactBottom.value)
            }
        })

        return {
            keyInfo,
            keyDataOptions,
            selectedKeyDataOption,
            showItem,
            exposeeHasFinancingLink,
            isScreenM,
            isScreenL,
            mlmViewTrackingId,
            shareBarTopConfig,
            shareBarBottomConfig,
            trackFinancingLinkClick,
            trackMapAnchorClick,
            getComponentByName,
            PositionType,
            StickyPosition,
            PageType,
            List_Wrap,
            CarouselLabel,
            ContactBtnPosition,
            elContactTop,
            elContactBottom,
            stickyBarIsVisible,
            expanderBoxTrackingData,
            expanderBox,
            toggleTable,
            showTable,
        }
    },
})
